import React, { useState, useEffect, useRef, useCallback } from 'react';
import { PlusIcon, PencilIcon, TrashIcon, MagnifyingGlassIcon, MapPinIcon, ArrowPathIcon, WrenchScrewdriverIcon } from '@heroicons/react/24/outline';
import { useSearchParams } from 'react-router-dom';
import { sitesAPI } from '../services/api.ts';
import api from '../services/api.ts';

interface Site {
  id: string;
  name: string;
  address_line_1: string;
  address_line_2?: string;
  city: string;
  county?: string;
  postcode: string;
  country: string;
  job_number?: string;
  status: 'active' | 'inactive' | 'completed';
  zahara_project_id?: number;
  created_at: string;
  updated_at: string;
}

interface EquipmentOnHire {
  id: string;
  equipment_name: string;
  hire_start_date: string;
  hire_end_date: string;
  price_per_day: number;
  supplier_name: string;
  status: string;
  total_cost: number;
}

const Sites: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [sites, setSites] = useState<Site[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSite, setEditingSite] = useState<Site | null>(null);
  
  // Equipment panel state
  const [showEquipmentPanel, setShowEquipmentPanel] = useState(false);
  const [selectedSite, setSelectedSite] = useState<Site | null>(null);
  const [siteEquipment, setSiteEquipment] = useState<EquipmentOnHire[]>([]);
  const [equipmentLoading, setEquipmentLoading] = useState(false);
  
  // Hire modal state
  const [showHireModal, setShowHireModal] = useState(false);
  const [selectedHire, setSelectedHire] = useState<EquipmentOnHire | null>(null);
  
  // Zahara sync state
  const [zaharaConfigured, setZaharaConfigured] = useState(false);
  const [syncStatus, setSyncStatus] = useState<{
    hasSynced: boolean;
    lastSyncDate: string | null;
    syncStatus: string;
  } | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    address_line_1: '',
    address_line_2: '',
    city: '',
    county: '',
    postcode: '',
    country: 'GB',
    job_number: '',
    status: 'active' as 'active' | 'inactive' | 'completed'
  });

  const searchInputRef = useRef<HTMLInputElement>(null);
  const debouncedSearchRef = useRef<number | null>(null);

  const fetchSites = async (search = '', showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      const response = await sitesAPI.getAll();
      let filteredSites = response.data.sites || [];
      
      if (search) {
        filteredSites = filteredSites.filter((site: Site) =>
          site.name.toLowerCase().includes(search.toLowerCase()) ||
          site.address_line_1.toLowerCase().includes(search.toLowerCase()) ||
          site.city.toLowerCase().includes(search.toLowerCase()) ||
          site.postcode.toLowerCase().includes(search.toLowerCase()) ||
          (site.job_number && site.job_number.toLowerCase().includes(search.toLowerCase()))
        );
      }
      
      setSites(filteredSites);
    } catch (error) {
      console.error('Error fetching sites:', error);
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  const fetchSiteEquipment = async (siteId: string) => {
    try {
      setEquipmentLoading(true);
      const response = await api.get(`/hires?site_id=${siteId}&status=active`);
      setSiteEquipment(response.data.hires || []);
    } catch (error) {
      console.error('Error fetching site equipment:', error);
      setSiteEquipment([]);
    } finally {
      setEquipmentLoading(false);
    }
  };

  // Check if Zahara is configured
  const checkZaharaConfiguration = useCallback(async () => {
    try {
      const response = await api.get('/zahara/settings');
      const settings = response.data;
      const isConfigured = settings.api_key && settings.nominal_code_id && settings.tax_code_id;
      setZaharaConfigured(isConfigured);
      
      if (isConfigured) {
        // Also fetch sync status
        await fetchSyncStatus();
      }
    } catch (err: any) {
      console.log('Zahara configuration check failed:', err.response?.data?.error || err.message);
      setZaharaConfigured(false);
    }
  }, []);

  // Fetch sync status
  const fetchSyncStatus = async () => {
    try {
      const response = await api.get('/zahara/projects/sync-status');
      setSyncStatus(response.data.data);
    } catch (err: any) {
      console.log('Failed to fetch sync status:', err.response?.data?.error || err.message);
    }
  };

  // Handle project sync
  const handleSyncProjects = async () => {
    try {
      setSyncing(true);
      setSyncMessage(null);
      
      const response = await api.post('/zahara/projects/sync', {
        syncType: 'incremental' // Use incremental for better performance
      });
      
      if (response.data.success) {
        const stats = response.data.stats;
        const zaharaStats = stats.zaharaToPlanted;
        const plantedStats = stats.plantedToZahara;
        
        let message = `✅ Bidirectional sync completed!\n`;
        message += `📥 Zahara → Planted: ${zaharaStats.totalProcessed} projects (${zaharaStats.created} new, ${zaharaStats.updated} updated)\n`;
        message += `📤 Planted → Zahara: ${plantedStats.pushed} sites pushed`;
        
        if (plantedStats.errors > 0) {
          message += ` (${plantedStats.errors} errors)`;
        }
        
        setSyncMessage({ 
          type: 'success', 
          text: message
        });
        
        // Refresh sites and sync status
        await fetchSites('', false);
        await fetchSyncStatus();
      }
    } catch (err: any) {
      console.error('Sync failed:', err.response?.data?.error || err.message);
      setSyncMessage({ 
        type: 'error', 
        text: `❌ Sync failed: ${err.response?.data?.error || err.message}` 
      });
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    fetchSites();
    checkZaharaConfiguration();
  }, [checkZaharaConfiguration]);

  // Handle URL parameters for auto-opening modal
  useEffect(() => {
    const openModal = searchParams.get('open_modal');
    
    if (openModal === 'true') {
      setShowModal(true);
    }
    
    // Clear URL parameter after processing
    if (openModal) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('open_modal');
      window.history.replaceState({}, '', newUrl.toString());
    }
  }, [searchParams]);

  const handleSearchChange = () => {
    const value = searchInputRef.current?.value || '';
    
    if (debouncedSearchRef.current) {
      clearTimeout(debouncedSearchRef.current);
    }
    
    debouncedSearchRef.current = window.setTimeout(() => {
      fetchSites(value, false);
    }, 300);
  };

  useEffect(() => {
    return () => {
      if (debouncedSearchRef.current) {
        clearTimeout(debouncedSearchRef.current);
      }
    };
  }, []);

  const resetForm = () => {
    setFormData({
      name: '',
      address_line_1: '',
      address_line_2: '',
      city: '',
      county: '',
      postcode: '',
      country: 'GB',
      job_number: '',
      status: 'active' as 'active' | 'inactive' | 'completed'
    });
    setEditingSite(null);
  };

  const handleAddSite = () => {
    resetForm();
    setShowModal(true);
  };

  const handleEditSite = (site: Site) => {
    setFormData({
      name: site.name,
      address_line_1: site.address_line_1,
      address_line_2: site.address_line_2 || '',
      city: site.city,
      county: site.county || '',
      postcode: site.postcode,
      country: site.country,
      job_number: site.job_number || '',
      status: site.status
    });
    setEditingSite(site);
    setShowModal(true);
  };

  const handleShowEquipment = async (site: Site) => {
    setSelectedSite(site);
    setShowEquipmentPanel(true);
    await fetchSiteEquipment(site.id);
  };

  const handleOpenHireModal = (equipment: EquipmentOnHire) => {
    setSelectedHire(equipment);
    setShowHireModal(true);
  };


  const handleDeleteSite = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this site?')) return;

    try {
      await sitesAPI.delete(id);
      fetchSites();
    } catch (error) {
      console.error('Error deleting site:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (editingSite) {
        await sitesAPI.update(editingSite.id, formData);
      } else {
        await sitesAPI.create(formData);
      }

      setShowModal(false);
      resetForm();
      fetchSites();
    } catch (error) {
      console.error('Error saving site:', error);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'badge-success';
      case 'inactive': return 'badge-warning';
      case 'completed': return 'badge-info';
      default: return 'badge-info';
    }
  };


  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center">
          <MapPinIcon className="h-8 w-8 text-blue-600 mr-3" />
          <h1 className="text-3xl font-bold text-gray-900">Site Management</h1>
        </div>
        <div className="flex items-center gap-3">
          {/* View Toggle Buttons */}

          {/* Zahara Sync Button */}
          {zaharaConfigured && (
            <button
              onClick={handleSyncProjects}
              disabled={syncing}
              className="btn btn-outline btn-info"
            >
              <ArrowPathIcon className={`h-5 w-5 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Syncing...' : 'Sync Projects'}
            </button>
          )}
          
          <button
            onClick={handleAddSite}
            className="btn btn-primary"
          >
            <PlusIcon className="h-5 w-5" />
            Add Site
          </button>
        </div>
      </div>

      {/* Sync Status Message */}
      {zaharaConfigured && syncStatus && (
        <div className="mb-4">
          {syncStatus.hasSynced ? (
            <div className="text-sm text-gray-600">
              Last sync: {new Date(syncStatus.lastSyncDate!).toLocaleString()}
              {syncStatus.syncStatus === 'error' && (
                <span className="text-red-600 ml-2">❌ Sync failed</span>
              )}
            </div>
          ) : (
            <div className="text-sm text-orange-600 cursor-pointer" onClick={handleSyncProjects}>
              ⚠️ No projects synced yet. Click to sync from Zahara.
            </div>
          )}
        </div>
      )}

      {/* Sync Result Message */}
      {syncMessage && (
        <div className={`mb-4 p-3 rounded-lg ${
          syncMessage.type === 'success' 
            ? 'bg-green-100 text-green-800' 
            : 'bg-red-100 text-red-800'
        }`}>
          <pre className="whitespace-pre-wrap font-sans">{syncMessage.text}</pre>
        </div>
      )}

      {/* Search */}
      <div className="mb-6">
        <div className="relative">
          <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Search sites by name, address, city, postcode, or job number..."
            onChange={handleSearchChange}
            className="w-full input"
          />
        </div>
      </div>

      {/* Sites List or Map */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <>
          {sites.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">
                {searchInputRef.current?.value ? 'No sites found matching your search.' : 'No sites found. Create your first site to get started.'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {sites.map((site) => (
                <div 
                  key={site.id} 
                  className="bg-white dark:bg-gray-800 rounded-lg rounded-lg shadow-md border border-gray-200 p-6 hover:shadow-lg transition-all"
                >
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                      {site.job_number && (
                        <p className="text-sm text-gray-600 mb-2">Job: {site.job_number}</p>
                      )}
                        
                            <span className={`badge badge-outline ${getStatusColor(site.status)}`}>
                          {site.status.charAt(0).toUpperCase() + site.status.slice(1)}
                        </span>

                      </div>
                      <div className="flex items-center gap-3 mb-2 mt-2">
                        <h3 className="text-xl font-semibold text-gray-900">{site.name}</h3>
                       </div>
                                         
                      
                      <div className="text-sm text-gray-600 space-y-1 h-24">
                        <p>{site.address_line_1}</p>
                        {site.address_line_2 && <p>{site.address_line_2}</p>}
                        <p>{site.city}{site.county && `, ${site.county}`}</p>
                        <p>{site.postcode}</p>
                        <p className="text-xs text-gray-500">{site.country}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex justify-between items-center pt-4 border-t border-gray-200">
                    <div className="text-xs text-gray-500">
                      Created: {formatDate(site.created_at)}
                    </div>
                    <div className="flex items-center">
                      {/* Zahara Sync Status Icon */}
                      {zaharaConfigured && (
                        <div className="flex items-center mr-2">
                          {site.zahara_project_id ? (
                            <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center" title="Synced with Zahara">
                              <ArrowPathIcon className="h-4 w-4 text-green-600" />
                            </div>
                          ) : (
                            <div>&nbsp;</div>
                          )}
                        </div>
                      )}
                      <div className="flex gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleShowEquipment(site);
                        }}
                        className="text-green-600 hover:text-green-800 p-1"
                        title="View equipment on hire"
                      >
                        <WrenchScrewdriverIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditSite(site);
                        }}
                        className="text-blue-600 hover:text-blue-800 p-1"
                        title="Edit site"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteSite(site.id);
                        }}
                        className="text-red-600 hover:text-red-800 p-1"
                        title="Delete site"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Equipment Panel */}
      {showEquipmentPanel && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50">
          <div className="absolute right-0 top-0 h-full w-1/5 bg-white shadow-xl overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-gray-900">
                  Equipment on Hire
                </h2>
                <button
                  onClick={() => setShowEquipmentPanel(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-800">{selectedSite?.name}</h3>
                <p className="text-sm text-gray-600">{selectedSite?.address_line_1}, {selectedSite?.city}</p>
              </div>

              {equipmentLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                </div>
              ) : siteEquipment.length === 0 ? (
                <div className="text-center py-8">
                  <WrenchScrewdriverIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">No equipment currently on hire</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {siteEquipment.map((equipment) => (
                    <div key={equipment.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <div className="flex justify-between items-start mb-2">
                        <button
                          onClick={() => handleOpenHireModal(equipment)}
                          className="font-semibold text-blue-600 hover:text-blue-800 text-sm text-left hover:underline"
                        >
                          {equipment.equipment_name}
                        </button>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          equipment.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {equipment.status}
                        </span>
                      </div>
                      
                      <div className="space-y-2 text-sm text-gray-600">
                        <div className="flex justify-between">
                          <span>Supplier:</span>
                          <span className="font-medium">{equipment.supplier_name}</span>
                        </div>
                        
                        <div className="flex justify-between">
                          <span>Start Date:</span>
                          <span>{new Date(equipment.hire_start_date).toLocaleDateString()}</span>
                        </div>
                        
                        <div className="flex justify-between">
                          <span>End Date:</span>
                          <span className={`font-medium ${
                            new Date(equipment.hire_end_date) < new Date() ? 'text-red-600' : 'text-gray-900'
                          }`}>
                            {new Date(equipment.hire_end_date).toLocaleDateString()}
                          </span>
                        </div>
                        
                        <div className="flex justify-between">
                          <span>Daily Rate:</span>
                          <span className="font-medium">£{equipment.price_per_day}</span>
                        </div>
                        
                        <div className="flex justify-between border-t border-gray-200 pt-2">
                          <span className="font-semibold">Total Cost:</span>
                          <span className="font-bold text-primary-600">£{equipment.total_cost}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Hire Details Modal */}
      {showHireModal && selectedHire && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  Hire Details
                </h2>
                <button
                  onClick={() => setShowHireModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="space-y-6">
                {/* Equipment Information */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Equipment Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Equipment Name</label>
                      <p className="mt-1 text-sm text-gray-900">{selectedHire.equipment_name}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Status</label>
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        selectedHire.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {selectedHire.status}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Hire Details */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Hire Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Start Date</label>
                      <p className="mt-1 text-sm text-gray-900">{new Date(selectedHire.hire_start_date).toLocaleDateString()}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">End Date</label>
                      <p className={`mt-1 text-sm font-medium ${
                        new Date(selectedHire.hire_end_date) < new Date() ? 'text-red-600' : 'text-gray-900'
                      }`}>
                        {new Date(selectedHire.hire_end_date).toLocaleDateString()}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Daily Rate</label>
                      <p className="mt-1 text-sm text-gray-900">£{selectedHire.price_per_day}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Total Cost</label>
                      <p className="mt-1 text-sm font-bold text-primary-600">£{selectedHire.total_cost}</p>
                    </div>
                  </div>
                </div>

                {/* Supplier Information */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Supplier Information</h3>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Supplier Name</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedHire.supplier_name}</p>
                  </div>
                </div>

                {/* Site Information */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Site Information</h3>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Site Name</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedSite?.name}</p>
                  </div>
                  <div className="mt-2">
                    <label className="block text-sm font-medium text-gray-700">Site Address</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedSite?.address_line_1}, {selectedSite?.city}</p>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowHireModal(false)}
                  className="btn btn-soft"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    setShowHireModal(false);
                    // Navigate to hires page with this hire selected
                    window.location.href = `/app/hires?edit=${selectedHire.id}`;
                  }}
                  className="btn btn-primary"
                >
                  Edit Hire
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                {editingSite ? 'Edit Site' : 'Add New Site'}
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Site Name */}
                  <div className="md:col-span-2">
                    <label className="label">
                      Site Name *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full input"
                      required
                    />
                  </div>

                  {/* Address Line 1 */}
                  <div className="md:col-span-2">
                    <label className="label">
                      Address Line 1 *
                    </label>
                    <input
                      type="text"
                      value={formData.address_line_1}
                      onChange={(e) => setFormData(prev => ({ ...prev, address_line_1: e.target.value }))}
                      className="w-full input"
                      required
                    />
                  </div>

                  {/* Address Line 2 */}
                  <div className="md:col-span-2">
                    <label className="label">
                      Address Line 2
                    </label>
                    <input
                      type="text"
                      value={formData.address_line_2}
                      onChange={(e) => setFormData(prev => ({ ...prev, address_line_2: e.target.value }))}
                      className="w-full inputt"
                    />
                  </div>

                  {/* City */}
                  <div>
                    <label className="label">
                      City *
                    </label>
                    <input
                      type="text"
                      value={formData.city}
                      onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                      className="w-full input"
                      required
                    />
                  </div>

                  {/* County */}
                  <div>
                    <label className="label">
                      County
                    </label>
                    <input
                      type="text"
                      value={formData.county}
                      onChange={(e) => setFormData(prev => ({ ...prev, county: e.target.value }))}
                      className="w-full input"
                    />
                  </div>

                  {/* Postcode */}
                  <div>
                    <label className="label">
                      Postcode *
                    </label>
                    <input
                      type="text"
                      value={formData.postcode}
                      onChange={(e) => setFormData(prev => ({ ...prev, postcode: e.target.value }))}
                      className="w-full input"
                      required
                    />
                  </div>

                  {/* Country */}
                  <div>
                    <label className="label">
                      Country *
                    </label>
                    <select
                      value={formData.country}
                      onChange={(e) => setFormData(prev => ({ ...prev, country: e.target.value }))}
                      className="w-full input"
                      required
                    >
                      <option value="GB">United Kingdom</option>
                      <option value="US">United States</option>
                      <option value="AU">Australia</option>
                      <option value="CA">Canada</option>
                      <option value="IE">Ireland</option>
                    </select>
                  </div>

                  {/* Job Number */}
                  <div>
                    <label className="label">
                      Job Number
                    </label>
                    <input
                      type="text"
                      value={formData.job_number}
                      onChange={(e) => setFormData(prev => ({ ...prev, job_number: e.target.value }))}
                      className="w-full input"
                    />
                  </div>

                  {/* Status */}
                  <div>
                    <label className="label">
                      Status
                    </label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value as 'active' | 'inactive' | 'completed' }))}
                      className="w-full select"
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="completed">Completed</option>
                    </select>
                  </div>
                </div>

                <div className="flex justify-end gap-4 pt-6">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="btn btn-soft"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                  >
                    {editingSite ? 'Update Site' : 'Create Site'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Sites;